import { useState, useEffect } from 'react';
import Breadcrumb from '@/components/base/Breadcrumb';

const FORM_URL = 'https://readdy.ai/api/form/d9rb3t6e3oq4jqumtv4g';

const milestones = [
  { year: '2018', title: '网站创立', desc: '由一群热爱火影忍者的同人创作者共同发起，致力于打造中文圈最专业的火影同人文化参考平台。' },
  { year: '2019', title: '内容扩展', desc: '从最初的角色分析板块，逐步扩展至画风鉴赏、创作技巧、收藏指南等多个维度。' },
  { year: '2020', title: '社区建立', desc: '建立了活跃的火影同人爱好者社群，累计服务超过50,000名火影粉丝。' },
  { year: '2021', title: '合作拓展', desc: '与多家同人印刷所和周边厂商建立合作关系，为读者提供更丰富的收藏与创作资源。' },
  { year: '2022', title: '内容升级', desc: '推出深度系列文章，系统梳理火影同人文化发展史，获得广泛好评。' },
  { year: '2023', title: '持续成长', desc: '文章累计阅读量突破百万，成为火影同人领域最具影响力的中文参考资料之一。' },
];

export default function AboutPage() {
  const [formStatus, setFormStatus] = useState<'idle' | 'success' | 'error'>('idle');
  const [formError, setFormError] = useState('');

  const handleSubmit = async (e: React.FormEvent<HTMLFormElement>) => {
    e.preventDefault();
    const form = e.currentTarget;
    const honeypot = (form.querySelector('[name="website_alt"]') as HTMLInputElement)?.value?.trim();

    if (honeypot) {
      setFormStatus('success');
      form.reset();
      return;
    }

    try {
      const formData = new FormData(form);
      const response = await fetch(FORM_URL, {
        method: 'POST',
        headers: { 'Content-Type': 'application/x-www-form-urlencoded' },
        body: new URLSearchParams(
          Array.from(formData.entries())
            .filter(([key]) => key !== 'website_alt')
            .map(([k, v]) => [k, v.toString()])
        ).toString(),
      });
      const responseText = await response.text();
      let parsed: any = {};
      try { parsed = JSON.parse(responseText); } catch {}

      if (response.ok && parsed?.code === 'OK') {
        setFormStatus('success');
        form.reset();
      } else {
        const serverMsg = parsed?.meta?.message || parsed?.message || parsed?.meta?.detail || responseText;
        setFormError(typeof serverMsg === 'string' ? serverMsg : '提交失败，请稍后再试');
        setFormStatus('error');
      }
    } catch {
      setFormError('网络错误，请检查连接后重试');
      setFormStatus('error');
    }
  };

  const orgSchema = {
    '@context': 'https://schema.org',
    '@type': 'Organization',
    name: '火影同人指南',
    url: 'https://naruto-doujin.com',
    description: '火影粉丝与同人爱好者的专业参考资料平台',
    contactPoint: {
      '@type': 'ContactPoint',
      contactType: 'customer service',
      email: 'contact@naruto-doujin.com',
      availableLanguage: ['Chinese'],
    },
  };

  const webpageSchema = {
    '@context': 'https://schema.org',
    '@type': 'AboutPage',
    name: '关于我们 - 火影同人指南',
    description: '了解火影同人指南的品牌故事、发展历程与联系方式',
  };

  return (
    <main className="min-h-screen pt-24">
      <script
        type="application/ld+json"
        dangerouslySetInnerHTML={{ __html: JSON.stringify(orgSchema) }}
      />
      <script
        type="application/ld+json"
        dangerouslySetInnerHTML={{ __html: JSON.stringify(webpageSchema) }}
      />
      <div className="w-full px-4 md:px-6 max-w-4xl mx-auto">
        <div className="mb-6">
          <Breadcrumb
            items={[
              { label: '首页', href: '/' },
              { label: '关于我们' },
            ]}
          />
        </div>

        <section className="mb-14">
          <h1 className="font-heading text-2xl md:text-3xl lg:text-4xl font-bold text-foreground-900 mb-6">
            关于我们
          </h1>
          <div className="text-foreground-700 leading-relaxed">
            <p className="text-base mb-4">
              <strong>火影同人指南</strong> 成立于2018年，是一个专注于火影忍者同人文化的中文参考平台。
              我们的使命是系统性地梳理和分享火影同人创作领域的知识与经验，
              帮助更多火影粉丝深入了解同人文化的魅力。
            </p>
            <p className="text-base mb-4">
              无论你是刚刚接触同人文化的新人，还是经验丰富的同人创作者，我们都希望这个平台能够为你提供有价值的参考。
              从角色设定分析到画风流派鉴赏，从创作技巧分享到收藏市场解读，
              我们致力于覆盖火影同人文化的每一个重要维度。
            </p>
            <p className="text-base">
              我们的团队由资深火影粉丝、同人创作者和动漫文化研究者组成。每一位成员都怀着对火影忍者的深厚热爱，
              以严谨的态度和专业的精神，为读者呈现高质量的参考内容。
            </p>
          </div>
        </section>

        <section className="mb-14">
          <h2 className="font-heading text-xl md:text-2xl font-bold text-foreground-900 mb-8">
            发展历程
          </h2>
          <div className="relative">
            <div className="absolute left-4 md:left-6 top-0 bottom-0 w-px bg-background-200"></div>
            <div className="space-y-8">
              {milestones.map((m, i) => (
                <div key={i} className="relative pl-12 md:pl-16">
                  <div className="absolute left-2.5 md:left-[21px] top-1 w-3 h-3 rounded-full bg-primary-500 border-2 border-background-50"></div>
                  <span className="text-sm font-bold text-primary-500">{m.year}</span>
                  <h3 className="font-heading text-lg font-semibold text-foreground-900 mt-1">
                    {m.title}
                  </h3>
                  <p className="mt-2 text-sm text-foreground-600 leading-relaxed">
                    {m.desc}
                  </p>
                </div>
              ))}
            </div>
          </div>
        </section>

        <section className="mb-14">
          <h2 className="font-heading text-xl md:text-2xl font-bold text-foreground-900 mb-8">
            行业口碑
          </h2>
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-5">
            <div className="bg-background-50 rounded-lg p-6 border border-background-200/70">
              <div className="flex items-center gap-1 text-secondary-500 mb-3">
                {[...Array(5)].map((_, i) => (
                  <i key={i} className="ri-star-fill text-sm"></i>
                ))}
              </div>
              <p className="text-sm text-foreground-600 leading-relaxed mb-3">
                "作为火影同人创作者，这个网站给了我很多灵感和实用的创作指导。角色分析板块尤其出色。"
              </p>
              <span className="text-xs text-foreground-400">—— 同人画师 林同学</span>
            </div>
            <div className="bg-background-50 rounded-lg p-6 border border-background-200/70">
              <div className="flex items-center gap-1 text-secondary-500 mb-3">
                {[...Array(5)].map((_, i) => (
                  <i key={i} className="ri-star-fill text-sm"></i>
                ))}
              </div>
              <p className="text-sm text-foreground-600 leading-relaxed mb-3">
                "收藏指南板块帮我避免了很多新手误区。品相鉴定和保存方法的部分非常实用！"
              </p>
              <span className="text-xs text-foreground-400">—— 同人收藏家 张先生</span>
            </div>
            <div className="bg-background-50 rounded-lg p-6 border border-background-200/70">
              <div className="flex items-center gap-1 text-secondary-500 mb-3">
                {[...Array(5)].map((_, i) => (
                  <i key={i} className="ri-star-fill text-sm"></i>
                ))}
              </div>
              <p className="text-sm text-foreground-600 leading-relaxed mb-3">
                "文化起源板块的内容深度令人惊叹，帮我完成了关于火影同人文化的研究论文。"
              </p>
              <span className="text-xs text-foreground-400">—— 动漫文化研究者 王老师</span>
            </div>
          </div>
        </section>

        <section className="mb-16">
          <h2 className="font-heading text-xl md:text-2xl font-bold text-foreground-900 mb-8">
            联系我们
          </h2>
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-8">
            <div>
              <div className="space-y-4">
                <div className="flex items-start gap-3">
                  <div className="w-10 h-10 flex items-center justify-center rounded-lg bg-primary-50 text-primary-500 flex-shrink-0">
                    <i className="ri-mail-line"></i>
                  </div>
                  <div>
                    <h3 className="font-medium text-foreground-900 text-sm">电子邮箱</h3>
                    <p className="text-sm text-foreground-500">contact@naruto-doujin.com</p>
                  </div>
                </div>
                <div className="flex items-start gap-3">
                  <div className="w-10 h-10 flex items-center justify-center rounded-lg bg-primary-50 text-primary-500 flex-shrink-0">
                    <i className="ri-map-pin-line"></i>
                  </div>
                  <div>
                    <h3 className="font-medium text-foreground-900 text-sm">地址</h3>
                    <p className="text-sm text-foreground-500">中国·北京市朝阳区</p>
                  </div>
                </div>
                <div className="flex items-start gap-3">
                  <div className="w-10 h-10 flex items-center justify-center rounded-lg bg-primary-50 text-primary-500 flex-shrink-0">
                    <i className="ri-time-line"></i>
                  </div>
                  <div>
                    <h3 className="font-medium text-foreground-900 text-sm">工作时间</h3>
                    <p className="text-sm text-foreground-500">周一至周五 9:00 - 18:00</p>
                  </div>
                </div>
              </div>
            </div>

            <div>
              <form
                data-readdy-form
                id="contact-form"
                onSubmit={handleSubmit}
                className="bg-background-50 rounded-lg p-6 border border-background-200/70"
              >
                <h3 className="font-heading font-semibold text-foreground-900 mb-4">
                  发送消息
                </h3>

                {formStatus === 'success' && (
                  <div className="mb-4 p-3 bg-green-50 text-green-700 rounded-lg text-sm">
                    消息已成功发送，我们会尽快回复！
                  </div>
                )}
                {formStatus === 'error' && (
                  <div className="mb-4 p-3 bg-red-50 text-red-700 rounded-lg text-sm">
                    {formError}
                  </div>
                )}

                <div className="space-y-4">
                  <div>
                    <label htmlFor="contact-name" className="block text-sm font-medium text-foreground-700 mb-1">
                      姓名
                    </label>
                    <input
                      type="text"
                      id="contact-name"
                      name="name"
                      required
                      className="w-full px-4 py-2.5 rounded-lg border border-background-200 bg-background-50 text-sm text-foreground-800 focus:outline-none focus:border-primary-400 focus:ring-1 focus:ring-primary-400 transition-colors"
                      placeholder="请输入您的姓名"
                    />
                  </div>
                  <div>
                    <label htmlFor="contact-email" className="block text-sm font-medium text-foreground-700 mb-1">
                      邮箱
                    </label>
                    <input
                      type="email"
                      id="contact-email"
                      name="email"
                      required
                      className="w-full px-4 py-2.5 rounded-lg border border-background-200 bg-background-50 text-sm text-foreground-800 focus:outline-none focus:border-primary-400 focus:ring-1 focus:ring-primary-400 transition-colors"
                      placeholder="请输入您的邮箱"
                    />
                  </div>
                  <div>
                    <label htmlFor="contact-message" className="block text-sm font-medium text-foreground-700 mb-1">
                      消息
                    </label>
                    <textarea
                      id="contact-message"
                      name="message"
                      rows={4}
                      required
                      maxLength={500}
                      className="w-full px-4 py-2.5 rounded-lg border border-background-200 bg-background-50 text-sm text-foreground-800 focus:outline-none focus:border-primary-400 focus:ring-1 focus:ring-primary-400 transition-colors resize-none"
                      placeholder="请输入您的消息（最多500字）"
                    ></textarea>
                  </div>

                  <div className="honeypot-wrapper">
                    <input
                      type="text"
                      name="website_alt"
                      tabIndex={-1}
                      autoComplete="off"
                      aria-hidden="true"
                      readOnly
                    />
                  </div>

                  <button
                    type="submit"
                    className="whitespace-nowrap w-full bg-primary-500 text-white py-2.5 rounded-lg font-medium text-sm hover:bg-primary-600 transition-colors cursor-pointer"
                  >
                    发送消息
                  </button>
                </div>
              </form>
            </div>
          </div>
        </section>
      </div>
    </main>
  );
}